import { Header } from '../../components/layout/header';
import { Footer } from '../../components/layout/footer';
import { SearchInput } from '../../components/search-input';
import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { PostCallModal } from '../../components/post-call-form/post-call-modal';
import { useLandingPageCollections, LandingPageCollectionsResponse } from '../../hooks/api/useContentCollection';
import { CollectionSummary } from '../../components/collection-summary';
import { SeeMoreButton } from '../../components/see-more-button';
import { ROUTE_PATH } from '../../routes/route-paths';
import { ContentSection } from '../../components/content-section';
import { VideoExperienceTile } from '../../components/video-experience-tile';
import { AudioExperienceTile } from '../../components/audio-experience-tile';
import { ResourceTile } from '../../components/resource-tile';
import { DropdownPill } from '../../components/dropdown-pill';
import { PeerAvatar } from '../../components/peer-avatar';
import { useAlgoliaSearch, AlgoliaSearchResult } from '../../hooks/api/useAlgoliaSearch';
import { Content, Resource, People } from '../../hooks/api/useExperiences';
import { createLoadMoreHandler } from '../../utils/constants';
import { useTagGroups, TagGroup } from '../../hooks/api/useTagGroups';
import useAuth from '../../hooks/utils/useAuth';
import { useSessionStorage } from '../../hooks/utils/useSessionStorage';
import { useResourcesByTopic } from '../../hooks/api/useBrowseResources';
import { useProviderPlan } from '../../hooks/api/useExternalProviderPlans';
import { useProviderResources } from '../../hooks/utils/useProviderResources';
import {
  getTopicCardComponent,
  getTopicCardTitle,
  slugToTagGroupKey,
  BROWSE_TOPIC_SLUGS,
  getTopicSlugByKey,
} from '../../utils/topic-utils';
import { TopicFilterDropdown } from '../../components/topic-filter-dropdown';
import { PeerListenersHeroIcon, StoriesHeroIcon, ResourcesHeroIcon, TooltipIcon } from '../../assets/images/svgs';
import { useMediaQuery } from '../../hooks/utils/useMediaQuery';
import { Features, useFeatureFlag } from '../../hooks/api/useFeatureFlag';
import { MatchStyleTrialDropdown } from '../../components/match-style-trial-dropdown';
import { useMatchStyleTrial } from '../../components/peer-listener-match-hint';

type ContentType = undefined | 'video' | 'audio' | 'resources' | 'peers';

const VIDEO_ITEMS = 6;
const AUDIO_ITEMS = 4;
const RESOURCE_ITEMS = 4;
const PEER_ITEMS = 6;

const PrimaryLandingPage = () => {
  const history = useHistory();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [contentTypeFilter, setContentTypeFilter] = useState<ContentType>(undefined);
  const [selectedTagGroupId, setSelectedTagGroupId] = useState<number | undefined>(undefined);
  const matchPreviewEnabled = useFeatureFlag(Features.SEARCH_MATCH_EXPLAINABILITY).enabled;
  const { matchStyle, setMatchStyle } = useMatchStyleTrial(matchPreviewEnabled);
  const [visibleTopicCollectionsCount, setVisibleTopicCollectionsCount] = useState(1);
  const [visibleVerticalCollectionsCount, setVisibleVerticalCollectionsCount] = useState(1);
  const { data: collectionsData, isLoading: isLoadingCollections } = useLandingPageCollections();

  // Check for postCallId in URL query params and open modal immediately
  const [postCallIdFromUrl, setPostCallIdFromUrl] = useState<number | undefined>(undefined);
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const postCallIdParam = searchParams.get('postCallId');
    if (postCallIdParam) {
      const postCallId = Number(postCallIdParam);
      if (!isNaN(postCallId)) {
        setPostCallIdFromUrl(postCallId);
        // Remove the query parameter from URL without reloading
        history.replace({ search: '' });
      }
    }
  }, [location.search, history]);
  const { data: topicTagGroups } = useTagGroups();

  const [isBrowseTopicsOpen, setIsBrowseTopicsOpen] = useState(false);
  const browseTopicsRef = useRef<HTMLDivElement>(null);
  const browseTopicsButtonRef = useRef<HTMLButtonElement>(null);
  const browseTopicsMobileRef = useRef<HTMLDivElement>(null);

  // Algolia search hook - only enabled when searchQuery has a value
  const trimmedQuery = searchQuery?.trim() || '';
  const {
    data: searchData,
    isLoading: isLoadingSearch,
    error: searchError,
  } = useAlgoliaSearch(trimmedQuery, selectedTagGroupId);

  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const handleBackToHome = useCallback(() => {
    setSearchQuery('');
    setContentTypeFilter(undefined);
    setSelectedTagGroupId(undefined);
  }, []);

  const handleBrowseTopicsToggle = useCallback(() => {
    setIsBrowseTopicsOpen((prev) => !prev);
  }, []);

  const handleBrowseTopicClick = useCallback(
    (tagGroupKey: string) => {
      setIsBrowseTopicsOpen(false);
      const slug = getTopicSlugByKey(tagGroupKey);
      history.push(`${ROUTE_PATH.BROWSE_TOPIC}/${slug}`);
    },
    [history],
  );

  const dfMdMedia = useMediaQuery('md');

  // Close browse topics dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!isBrowseTopicsOpen) return;

      const target = event.target as Node;

      // Check desktop dropdown
      if (dfMdMedia) {
        if (
          browseTopicsRef.current &&
          !browseTopicsRef.current.contains(target) &&
          browseTopicsButtonRef.current &&
          !browseTopicsButtonRef.current.contains(target)
        ) {
          setIsBrowseTopicsOpen(false);
        }
      } else {
        // Check mobile dropdown
        if (browseTopicsMobileRef.current && !browseTopicsMobileRef.current.contains(target)) {
          setIsBrowseTopicsOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isBrowseTopicsOpen, dfMdMedia]);

  // Group search results by type
  const groupedResults = useMemo(() => {
    if (!searchData?.data) {
      return {
        video: [] as Content[],
        audio: [] as Content[],
        resources: [] as Resource[],
        peers: [] as People[],
      };
    }

    const video: Content[] = [];
    const audio: Content[] = [];
    const resources: Resource[] = [];
    const peers: People[] = [];

    searchData.data.forEach((item) => {
      if (item.source === 'content') {
        const content = item as Content & { source: 'content' };
        const mediaType = 'media_type' in content ? content.media_type : undefined;
        if (mediaType === 'VIDEO') {
          video.push(content);
        } else if (mediaType === 'AUDIO') {
          audio.push(content);
        }
      } else if (item.source === 'resources') {
        const resource = item as Resource & { source: 'resources' };
        resources.push(resource);
      } else if (item.source === 'user') {
        const peer = item as People & { source: 'user' };
        peers.push(peer);
      }
    });

    return { video, audio, resources, peers };
  }, [searchData]);

  // Items shown state with initial values
  const [itemsShown, setItemsShown] = useState({
    video: VIDEO_ITEMS,
    audio: AUDIO_ITEMS,
    resources: RESOURCE_ITEMS,
    peers: PEER_ITEMS,
  });

  // Reset itemsShown when filters or search query changes
  useEffect(() => {
    setItemsShown({
      video: VIDEO_ITEMS,
      audio: AUDIO_ITEMS,
      resources: RESOURCE_ITEMS,
      peers: PEER_ITEMS,
    });
  }, [contentTypeFilter, searchQuery, selectedTagGroupId]);

  // Load more handlers
  const loadMoreVideos = createLoadMoreHandler(setItemsShown, 'video', VIDEO_ITEMS, groupedResults.video.length);
  const loadMoreAudio = createLoadMoreHandler(setItemsShown, 'audio', AUDIO_ITEMS, groupedResults.audio.length);
  const loadMoreResources = createLoadMoreHandler(
    setItemsShown,
    'resources',
    RESOURCE_ITEMS,
    groupedResults.resources.length,
  );
  const loadMorePeers = createLoadMoreHandler(setItemsShown, 'peers', PEER_ITEMS, groupedResults.peers.length);

  // Get counts from backend response (with backward compatibility for old format)
  const videoCount =
    searchData?.counts_by_source?.video ??
    (searchData?.counts_by_source?.content
      ? Math.floor(
          (searchData.counts_by_source.content * groupedResults.video.length) /
            (groupedResults.video.length + groupedResults.audio.length || 1),
        )
      : groupedResults.video.length);
  const audioCount =
    searchData?.counts_by_source?.audio ??
    (searchData?.counts_by_source?.content
      ? Math.floor(
          (searchData.counts_by_source.content * groupedResults.audio.length) /
            (groupedResults.video.length + groupedResults.audio.length || 1),
        )
      : groupedResults.audio.length);
  const resourcesCount = searchData?.counts_by_source?.resources ?? groupedResults.resources.length;
  const peersCount =
    searchData?.counts_by_source?.peers ?? searchData?.counts_by_source?.user ?? groupedResults.peers.length;

  // Filter visibility based on contentTypeFilter
  const showVideo = contentTypeFilter === undefined || contentTypeFilter === 'video';
  const showAudio = contentTypeFilter === undefined || contentTypeFilter === 'audio';
  const showResources = contentTypeFilter === undefined || contentTypeFilter === 'resources';
  const showPeers = contentTypeFilter === undefined || contentTypeFilter === 'peers';

  // Get available content type options based on search results
  const availableContentTypeOptions = useMemo(() => {
    if (isLoadingSearch) return [];
    return [
      { value: 'video', label: 'Video Stories', hasResults: groupedResults.video.length > 0 },
      { value: 'audio', label: 'Audio Stories', hasResults: groupedResults.audio.length > 0 },
      { value: 'resources', label: 'Resources', hasResults: groupedResults.resources.length > 0 },
      { value: 'peers', label: 'Peers', hasResults: groupedResults.peers.length > 0 },
    ]
      .filter((opt) => opt.hasResults)
      .map(({ hasResults, ...opt }) => opt);
  }, [groupedResults, isLoadingSearch]);

  // Extract unique tag group names from search results
  const availableTagGroupNames = useMemo(() => {
    if (!searchData?.data) return new Set<string>();
    const getTagGroups = (item: AlgoliaSearchResult): string[] => {
      if (item.source === 'content') return (item as Content & { source: 'content' }).tag_group_names || [];
      if (item.source === 'resources') return (item as Resource & { source: 'resources' } as any).tag_group_names || [];
      if (item.source === 'user') return (item as People & { source: 'user' }).listener_audio_tag_groups || [];
      return [];
    };
    return new Set(searchData.data.flatMap(getTagGroups));
  }, [searchData]);

  // Filter topic tag groups to only those that appear in search results
  const availableTopicTagGroups = useMemo(() => {
    if (!topicTagGroups) {
      return [];
    }

    // If loading or no tag group names found, return empty array
    if (isLoadingSearch || availableTagGroupNames.size === 0) {
      return [];
    }

    // Find matching tag groups (case-insensitive matching)
    const availableNamesLower = new Set(Array.from(availableTagGroupNames).map((name) => name.toLowerCase()));
    return topicTagGroups.filter((tg) => availableNamesLower.has(tg.name.toLowerCase()));
  }, [topicTagGroups, availableTagGroupNames, isLoadingSearch]);

  // Get selected tag group name from selectedTagGroupId
  const selectedTagGroupName = useMemo(() => {
    if (!selectedTagGroupId || !topicTagGroups) return undefined;
    return topicTagGroups.find((tg) => tg.id === selectedTagGroupId)?.name;
  }, [selectedTagGroupId, topicTagGroups]);

  // Clear content type filter if selected option is no longer available
  useEffect(() => {
    if (!isLoadingSearch && contentTypeFilter !== undefined) {
      const isAvailable = availableContentTypeOptions.some((opt) => opt.value === contentTypeFilter);
      if (!isAvailable) {
        setContentTypeFilter(undefined);
      }
    }
  }, [availableContentTypeOptions, contentTypeFilter, isLoadingSearch]);

  // Clear topic filter if selected option is no longer available
  useEffect(() => {
    if (!isLoadingSearch && selectedTagGroupId !== undefined) {
      const isAvailable = availableTopicTagGroups.some((tg) => tg.id === selectedTagGroupId);
      if (!isAvailable) {
        setSelectedTagGroupId(undefined);
      }
    }
  }, [availableTopicTagGroups, selectedTagGroupId, isLoadingSearch]);

  const hasSearchQuery = trimmedQuery.length > 0;
  const hasSearchResults = searchData && searchData.count > 0;
  // Show search results section whenever there's a query, even if no results (to show "No results found" message)
  const showSearchResults = hasSearchQuery && (searchData !== undefined || isLoadingSearch);

  // Check if the first result is a peer to prioritize the Peers section
  const isFirstResultPeer = useMemo(() => {
    return searchData?.data && searchData.data.length > 0 && searchData.data[0].source === 'user';
  }, [searchData]);

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <Header />
      <div className={`flex-1 ${!dfMdMedia ? 'pt-[76px]' : ''}`}>
        <div className={`max-w-page mx-auto flex items-center px-6 pt-8 ${dfMdMedia ? 'flex-row gap-7' : 'flex-col'}`}>
          <SearchInput onSearch={handleSearch} initialValue={searchQuery || ''} className="mb-0" />
          {dfMdMedia ? (
            <div ref={browseTopicsRef} className="relative">
              <button
                ref={browseTopicsButtonRef}
                onClick={handleBrowseTopicsToggle}
                className="text-primary font-albert-sans text-xl font-medium leading-6 transition-opacity hover:opacity-80"
              >
                Browse Topics
              </button>
              {isBrowseTopicsOpen && topicTagGroups && topicTagGroups.length > 0 && (
                <div className="border-input-border-gray absolute left-0 top-full z-50 mt-2 w-[343px] rounded-xl border bg-white p-2 shadow-lg">
                  <div className="flex max-h-[400px] flex-col gap-1.5 overflow-y-auto">
                    {topicTagGroups.map((tagGroup) => (
                      <button
                        key={tagGroup.id}
                        onClick={() => handleBrowseTopicClick(tagGroup.key)}
                        className="hover:bg-input-border-gray group flex h-8 cursor-pointer items-center gap-1.5 rounded-lg px-2 py-2 text-left transition-colors"
                      >
                        <span
                          className="font-open-sans group-hover:text-primary leading-3.5 text-body-gray text-sm font-medium transition-colors"
                          style={{ fontStyle: 'normal' }}
                        >
                          {tagGroup.name}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>

        {showSearchResults ? (
          <div className="max-w-page mx-auto px-6 py-8">
            {/* Back To Home link */}
            <button onClick={handleBackToHome} className="text-primary mb-4 text-sm hover:text-gray-700">
              &lt; Back To Home
            </button>

            {/* Top Search Results title */}
            <h1 className="font-albert-sans mb-6 text-[28px] font-semibold leading-7 text-black">Top Search Results</h1>

            {/* Filters */}
            <div className={`mb-8 flex items-start justify-start gap-3 ${dfMdMedia ? 'flex-row' : 'flex-col'}`}>
              <DropdownPill
                label="Content type"
                placeholder="All Content"
                options={availableContentTypeOptions}
                selectedValue={contentTypeFilter}
                onSelect={(value) => setContentTypeFilter(value as ContentType)}
              />
              <TopicFilterDropdown
                selectedTagGroupId={selectedTagGroupId}
                onSelect={(value) => setSelectedTagGroupId(value as number | undefined)}
                topicTagGroups={availableTopicTagGroups}
              />
              {matchPreviewEnabled ? (
                <MatchStyleTrialDropdown selectedValue={matchStyle} onSelect={setMatchStyle} />
              ) : null}
            </div>
            {matchPreviewEnabled ? (
              <p className="font-open-sans mb-6 text-sm text-gray-500">
                Match styles are a preview for your client. Search results show why each item matched.
              </p>
            ) : null}

            {searchError ? (
              <div className="text-center text-red-500">Error loading search results. Please try again.</div>
            ) : (
              <>
                {/* Render Peers first if the first result is a peer */}
                {isFirstResultPeer && (
                  <ContentSection
                    title="Peers"
                    titleCount={peersCount}
                    items={groupedResults.peers}
                    itemsShown={itemsShown.peers}
                    totalCount={peersCount}
                    onLoadMore={loadMorePeers}
                    shouldShow={showPeers && (groupedResults.peers.length > 0 || isLoadingSearch)}
                    isLoading={isLoadingSearch}
                    renderItem={(item, index) => (
                      <PeerAvatar
                        key={item?.listener_role_id || `skeleton-peer-${index}`}
                        peer={item || undefined}
                        isLoading={!item}
                        matchStyle={matchStyle}
                      />
                    )}
                  />
                )}

                <ContentSection
                  title="Top Video Stories"
                  titleCount={videoCount}
                  items={groupedResults.video}
                  itemsShown={itemsShown.video}
                  totalCount={videoCount}
                  onLoadMore={loadMoreVideos}
                  shouldShow={showVideo && (groupedResults.video.length > 0 || isLoadingSearch)}
                  isLoading={isLoadingSearch}
                  renderItem={(item, index) => (
                    <VideoExperienceTile
                      key={item?.id || `skeleton-video-${index}`}
                      experience={item as Content | undefined}
                      handleScheduleModalOpen={() => {}}
                      selectedTagGroupName={selectedTagGroupName}
                      isLoading={!item}
                      matchStyle={matchStyle}
                    />
                  )}
                />

                <ContentSection
                  title="Top Audio Stories"
                  titleCount={audioCount}
                  items={groupedResults.audio}
                  itemsShown={itemsShown.audio}
                  totalCount={audioCount}
                  onLoadMore={loadMoreAudio}
                  shouldShow={showAudio && (groupedResults.audio.length > 0 || isLoadingSearch)}
                  isLoading={isLoadingSearch}
                  renderItem={(item, index) => (
                    <AudioExperienceTile
                      key={item?.id || `skeleton-audio-${index}`}
                      experience={item as Content | undefined}
                      selectedTagGroupName={selectedTagGroupName}
                      isLoading={!item}
                      matchStyle={matchStyle}
                    />
                  )}
                />

                <ContentSection
                  title="Top Resources"
                  titleCount={resourcesCount}
                  items={groupedResults.resources}
                  itemsShown={itemsShown.resources}
                  totalCount={resourcesCount}
                  onLoadMore={loadMoreResources}
                  shouldShow={showResources && (groupedResults.resources.length > 0 || isLoadingSearch)}
                  isLoading={isLoadingSearch}
                  renderItem={(item, index) => (
                    <ResourceTile
                      key={item?.id || `skeleton-resource-${index}`}
                      resource={item || undefined}
                      id={item?.id}
                      image={item?.image_url}
                      resourceName={item?.name}
                      caption={item?.description}
                      tagGroupNames={(item as Resource & { source: 'resources' } as any)?.tag_group_names}
                      selectedTagGroupName={selectedTagGroupName}
                      isLoading={!item}
                      matchStyle={matchStyle}
                    />
                  )}
                />

                {/* Render Peers last if the first result is NOT a peer */}
                {!isFirstResultPeer && (
                  <ContentSection
                    title="Peers"
                    titleCount={peersCount}
                    items={groupedResults.peers}
                    itemsShown={itemsShown.peers}
                    totalCount={peersCount}
                    onLoadMore={loadMorePeers}
                    shouldShow={showPeers && (groupedResults.peers.length > 0 || isLoadingSearch)}
                    isLoading={isLoadingSearch}
                    renderItem={(item, index) => (
                      <PeerAvatar
                        key={item?.listener_role_id || `skeleton-peer-${index}`}
                        peer={item || undefined}
                        isLoading={!item}
                        matchStyle={matchStyle}
                      />
                    )}
                  />
                )}

                {!isLoadingSearch && !hasSearchResults && (
                  <div className="py-12 text-center text-gray-500">
                    <p className="text-lg">No results found.</p>
                    <p className="mt-2 text-sm">Try adjusting your search query.</p>
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
          <>
            <Collections
              collectionsData={collectionsData}
              isLoadingCollections={isLoadingCollections}
              topicTagGroups={topicTagGroups}
              visibleTopicCollectionsCount={visibleTopicCollectionsCount}
              setVisibleTopicCollectionsCount={setVisibleTopicCollectionsCount}
              visibleVerticalCollectionsCount={visibleVerticalCollectionsCount}
              setVisibleVerticalCollectionsCount={setVisibleVerticalCollectionsCount}
            />
            <HomeResourcesBanners />
            <ExploreByContent />
            <BrowseByGoal />
          </>
        )}
      </div>
      <Footer />
      {postCallIdFromUrl && (
        <PostCallModal
          open={!!postCallIdFromUrl}
          onClose={() => setPostCallIdFromUrl(undefined)}
          postCallId={postCallIdFromUrl}
        />
      )}
    </div>
  );
};

const RESOURCE_BANNER_ITEMS = 6;

type HomeResourceBannerProps<T> = {
  title: string;
  viewMoreHref: string;
  items: T[];
  isLoading: boolean;
  renderItem: (item: T | undefined, index: number) => React.ReactNode;
};

const HomeResourceBanner = <T,>({
  title,
  viewMoreHref,
  items,
  isLoading,
  renderItem,
}: HomeResourceBannerProps<T>): React.ReactElement => {
  const dfMdMedia = useMediaQuery('md');
  const history = useHistory();
  const displayItems = isLoading
    ? (Array(RESOURCE_BANNER_ITEMS).fill(undefined) as (T | undefined)[])
    : items.slice(0, RESOURCE_BANNER_ITEMS);

  return (
    <div>
      <div className="mb-[23px] flex h-[62px] items-end">
        <div className="flex items-center gap-3">
          <h2 className="font-albert-sans text-[28px] font-semibold leading-9 text-black">{title}</h2>
          <img src={TooltipIcon} alt="" className="size-[18px]" />
        </div>
      </div>

      {dfMdMedia ? (
        <div className="flex h-[322px] items-start gap-6 overflow-hidden">{displayItems.map(renderItem)}</div>
      ) : (
        <div className="h-124 flex gap-6 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {displayItems.map((item, index) => (
            <div key={index} data-carousel-item className="flex-shrink-0">
              {renderItem(item, index)}
            </div>
          ))}
        </div>
      )}

      <SeeMoreButton
        onClick={() => history.push(viewMoreHref)}
        buttonClassName="bg-button-secondary hover:bg-button-secondary-hover text-primary"
      />
    </div>
  );
};

const HomeResourcesBanners: React.FC = () => {
  const { user } = useAuth();
  const { isLoading: isLoadingProviderPlan } = useProviderPlan();
  const providerResources = useProviderResources();

  const clientId = useSessionStorage('clientId') || user?.caller_role?.active_subscription?.package?.client?.id;
  const clientName = user?.caller_role?.active_subscription?.package?.client?.name;

  const { data: resourcesData, isLoading: isLoadingResources } = useResourcesByTopic(undefined, Number(clientId));

  const clientResources = useMemo(() => {
    const resources = resourcesData?.data ?? [];
    return resources.filter((r) => r.client_id !== null);
  }, [resourcesData?.data]);

  const hasClinicalResources = providerResources.length > 0;
  const hasClientResources = clientResources.length > 0 && clientName !== undefined;

  if (!hasClinicalResources && !hasClientResources && !isLoadingProviderPlan && !isLoadingResources) {
    return null;
  }

  return (
    <div className="max-w-page mx-auto mb-[102px] space-y-[37px] px-6">
      {hasClinicalResources && (
        <HomeResourceBanner
          title="Clinical Resources"
          viewMoreHref={`${ROUTE_PATH.BROWSE_RESOURCES}?resource_type=licensed_care`}
          items={providerResources}
          isLoading={isLoadingProviderPlan}
          renderItem={(resource, index) => (
            <ResourceTile
              key={resource?.name || `skeleton-provider-${index}`}
              image={resource?.image_url}
              resourceName={resource?.name}
              caption={resource?.description}
              tagGroupNames={resource?.tag_names}
              providerResource={resource}
              isLoading={!resource}
            />
          )}
        />
      )}

      {hasClientResources && (
        <HomeResourceBanner
          title={`${clientName}'s Resources`}
          viewMoreHref={`${ROUTE_PATH.BROWSE_RESOURCES}?resource_type=client`}
          items={clientResources}
          isLoading={isLoadingResources}
          renderItem={(resource, index) => (
            <ResourceTile
              key={resource?.id || `skeleton-client-resource-${index}`}
              resource={resource || undefined}
              id={resource?.id}
              image={resource?.image_url}
              resourceName={resource?.name}
              caption={resource?.description}
              isGlobal={false}
              tagGroupNames={(resource as any)?.tag_group_names}
              isLoading={!resource}
            />
          )}
        />
      )}
    </div>
  );
};

interface CollectionsProps {
  collectionsData: LandingPageCollectionsResponse | undefined;
  isLoadingCollections: boolean;
  topicTagGroups: TagGroup[] | undefined;
  visibleTopicCollectionsCount: number;
  setVisibleTopicCollectionsCount: (count: number | ((prev: number) => number)) => void;
  visibleVerticalCollectionsCount: number;
  setVisibleVerticalCollectionsCount: (count: number | ((prev: number) => number)) => void;
}

const Collections: React.FC<CollectionsProps> = ({
  collectionsData,
  isLoadingCollections,
  topicTagGroups,
  visibleTopicCollectionsCount,
  setVisibleTopicCollectionsCount,
  visibleVerticalCollectionsCount,
  setVisibleVerticalCollectionsCount,
}) => {
  const getTagGroupNameFromCollection = (collection: any) => {
    if (!collection?.tags || !topicTagGroups?.length) return undefined;

    const collectionTagIds = new Set(collection.tags.map((tag: any) => tag.id));

    return topicTagGroups.find((tagGroup) =>
      tagGroup.tags?.some((tag: { id: number }) => collectionTagIds.has(tag.id)),
    );
  };

  const showPlaceholder = isLoadingCollections && !collectionsData;

  return (
    <div className="max-w-page mx-auto px-6 pb-0 pt-8">
      {showPlaceholder && (
        <div className="mb-[37px] flex flex-col gap-[3px]">
          <CollectionSummary isLoading />
        </div>
      )}
      {collectionsData?.topic_collections?.slice(0, visibleTopicCollectionsCount).map((collection, index) => {
        const tagGroup = getTagGroupNameFromCollection(collection);
        const collectionName = `Based on topic selection of ${tagGroup?.name!}`;
        const isLastVisibleCollection = index === visibleTopicCollectionsCount - 1;
        const totalTopicCollections = collectionsData?.topic_collections?.length || 0;
        const hasMoreCollections = visibleTopicCollectionsCount < totalTopicCollections;

        const handleOpenAdditional = () => {
          setVisibleTopicCollectionsCount((prev) => prev + 1);
        };

        return (
          <div key={collection.id} className="mb-[37px] flex flex-col gap-[3px]">
            <CollectionSummary
              collection={collection}
              collectionName={collectionName}
              selectedTagGroupName={tagGroup?.name}
              isLoading={false}
            />
            {isLastVisibleCollection && hasMoreCollections && (
              <SeeMoreButton
                onClick={handleOpenAdditional}
                text="Open additional preferred collections"
                className="mt-0"
                buttonClassName="bg-button-secondary hover:bg-button-secondary-hover text-primary"
              />
            )}
          </div>
        );
      })}

      {collectionsData?.vertical_collections?.slice(0, visibleVerticalCollectionsCount).map((collection, index) => {
        const collectionName = collection.name;
        const isLastVisibleCollection = index === visibleVerticalCollectionsCount - 1;
        const totalVerticalCollections = collectionsData?.vertical_collections?.length || 0;
        const hasMoreCollections = visibleVerticalCollectionsCount < totalVerticalCollections;

        const handleOpenAdditional = () => {
          setVisibleVerticalCollectionsCount((prev) => prev + 1);
        };

        return (
          <div key={collection.id} className="mb-[37px] flex flex-col gap-[3px]">
            <CollectionSummary
              collection={collection}
              collectionName={collectionName}
              isPrimaryLandingPage={true}
              isLoading={false}
            />
            {isLastVisibleCollection && hasMoreCollections && (
              <SeeMoreButton
                onClick={handleOpenAdditional}
                text="Open additional collections"
                className="mt-0"
                buttonClassName="bg-button-secondary hover:bg-button-secondary-hover text-primary"
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

const BrowseByGoal: React.FC = () => {
  const { data: topicTagGroups } = useTagGroups();
  const [showAllTopics, setShowAllTopics] = useState(false);

  const allGoals = BROWSE_TOPIC_SLUGS.map((slug) => {
    const tagGroupKey = slugToTagGroupKey[slug];
    const tagGroup = topicTagGroups?.find((tg) => tg.key === tagGroupKey);
    const CardComponent = getTopicCardComponent(tagGroup);
    const cardTitle = getTopicCardTitle(tagGroup);
    return {
      slug,
      link: `${ROUTE_PATH.BROWSE_TOPIC}/${slug}`,
      CardComponent,
      cardTitle,
    };
  });

  const goals = showAllTopics ? allGoals : allGoals.slice(0, 8);
  const handleSeeMore = () => {
    setShowAllTopics(true);
  };

  return (
    <div className="bg-connect-bg">
      <div className="max-w-page mx-auto space-y-8 px-6 pb-20 pt-10">
        <h2 className="font-albert-sans text-[28px] font-semibold leading-7 text-black">Connect by Goal</h2>
        <div className="grid grid-cols-2 gap-x-[15px] gap-y-[15px] pb-8 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {goals.map((goal, index) => {
            const Card = goal.CardComponent;
            return (
              <Link to={goal.link} key={index}>
                <div
                  key={index}
                  className="w-42 h-42 flex items-center justify-center rounded-xl bg-white sm:h-[250px] sm:w-full"
                >
                  {Card && <Card title={goal.cardTitle} className="h-full w-full" />}
                </div>
              </Link>
            );
          })}
        </div>
        {!showAllTopics && <SeeMoreButton onClick={handleSeeMore} />}
      </div>
    </div>
  );
};

interface ExploreByContentItem {
  iconComponent: React.ComponentType<{ className?: string }>;
  label: string;
  link: string;
}

const ExploreByContent: React.FC = () => {
  const contentItems: ExploreByContentItem[] = [
    {
      iconComponent: StoriesHeroIcon,
      label: 'Stories',
      link: ROUTE_PATH.BROWSE_STORIES,
    },
    {
      iconComponent: PeerListenersHeroIcon,
      label: 'Listeners',
      link: ROUTE_PATH.BROWSE_PEER_LISTENERS,
    },
    {
      iconComponent: ResourcesHeroIcon,
      label: 'Resources',
      link: ROUTE_PATH.BROWSE_RESOURCES,
    },
  ];
  const dfMdMedia = useMediaQuery('md');

  return (
    <div className="max-w-page mx-auto px-6 py-20">
      <h2 className="font-albert-sans mb-8 text-[28px] font-semibold leading-7 text-black">Explore by content</h2>
      <div className="flex flex-col gap-8 sm:!grid sm:grid-cols-2 md:grid-cols-4">
        {contentItems.map((item, index) => {
          const { iconComponent: IconComponent, label } = item;
          const content = (
            <>
              {dfMdMedia ? (
                <div className="bg-button-secondary hover:bg-button-secondary-hover flex h-[214px] w-[302px] flex-col items-center justify-center gap-4 rounded-xl">
                  <IconComponent className="h-[90px] w-[90px] shrink-0" />
                  <span className="font-albert-sans text-xl font-normal leading-6 text-black">{label}</span>
                </div>
              ) : (
                <div className="bg-button-secondary hover:bg-button-secondary-hover flex h-[88px] w-full items-center justify-start gap-4 rounded-xl px-6">
                  <IconComponent className="h-[50px] w-[50px] shrink-0" />
                  <span className="font-albert-sans text-lg font-normal leading-6 text-black">{label}</span>
                </div>
              )}
            </>
          );

          if (item.link) {
            return (
              <Link key={index} to={item.link} className="block">
                {content}
              </Link>
            );
          }

          return <div key={index}>{content}</div>;
        })}
      </div>
    </div>
  );
};

export default PrimaryLandingPage;
